import { type MarkdownDataMap } from '@atlassian/conversation-assistant-content-transformer/utils/stream-markdown-parser';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type StreamMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { addAnswerPartWithMarkdownDataMap } from './addAnswerPartWithMarkdownDataMap';
import { type AnswerUiPartsCloseResult, createAnswerUiPartsTracker } from './answer-ui-parts';
import { setAnswerUiSlots } from './setAnswerUiSlots';
import { type ChatStoreContainerProps } from './types';

/**
 * Send path only: the reconnect path ignores typed events, so cards appear only when
 * FINAL_RESPONSE re-renders the answer (CCSEARCH-7366).
 */
export type AnswerUiPartsStreamHandler = {
	/** Returns true only if the event changed the answer in the store. */
	handleStreamMessage: (streamMessage: StreamMessage) => boolean;
	/**
	 * LEGACY-INLINE-LUMINA: observe-only, to report responses that mix legacy markers
	 * with typed parts.
	 */
	observeParserMarkdownData: (markdownData: MarkdownDataMap) => void;
	/** Call after the FINAL_RESPONSE replace. */
	onFinalResponse: () => void;
	/**
	 * Call in `finally`. Settles slots still waiting for a payload so no card stays
	 * loading. Idempotent, a no-op after onFinalResponse, and never throws.
	 */
	onStreamEnd: () => void;
};

/**
 * Reports protocol errors, and parts still missing a payload at FINAL_RESPONSE (RCS
 * sends every payload before it). Missing payloads after an abort and unused payloads
 * are expected.
 */
const isAnswerUiPartsCloseAnomalous = (result: AnswerUiPartsCloseResult): boolean =>
	Object.keys(result.protocolErrors).length > 0 ||
	(result.reason === 'final' && result.partsWithoutPayload > 0);

export const createAnswerUiPartsStreamHandler = ({
	conversationId,
	getAnswerId,
	answerIndex,
	getMessages,
	setMessages,
	onUiPartPlaced,
}: {
	conversationId: string;
	/** Read on every write: the stream renames the message from its temporary id to the server id. */
	getAnswerId: () => string;
	answerIndex?: number;
	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	onUiPartPlaced: () => void;
}): AnswerUiPartsStreamHandler => {
	// Cards are additive, so a failed card write stops typed parts for this stream
	// instead of failing the text answer. Other stream writes aren't isolated: if they
	// fail, the answer is lost anyway.
	let disabled = false;

	const tracker = createAnswerUiPartsTracker({
		onEventAfterClose: () => {
			void logException({
				exception: new Error('Answer UI parts event received after the stream closed'),
				name: 'AnswerUiPartsProtocolError',
			});
		},
	});

	const reportClose = (closeResult: AnswerUiPartsCloseResult) => {
		if (!isAnswerUiPartsCloseAnomalous(closeResult)) {
			return;
		}
		const protocolErrorTags: Record<string, string> = {};
		for (const [error, count] of Object.entries(closeResult.protocolErrors)) {
			protocolErrorTags[`protocolError.${error}`] = String(count);
		}
		void logException({
			exception: new Error(
				'Answer UI parts stream closed with protocol errors or missing payloads',
			),
			name: 'AnswerUiPartsProtocolError',
			tags: {
				closeReason: closeResult.reason,
				partsWithoutPayload: String(closeResult.partsWithoutPayload),
				payloadsWithoutPart: String(closeResult.payloadsWithoutPart),
				...protocolErrorTags,
			},
		});
	};

	const tryWrite = (eventType: StreamMessage['type'], write: () => void): boolean => {
		try {
			write();
			return true;
		} catch (error) {
			disabled = true;
			void logException({
				exception: error,
				name: 'AnswerUiPartsStoreWriteError',
				tags: { eventType },
			});
			return false;
		}
	};

	const handleStreamMessage: AnswerUiPartsStreamHandler['handleStreamMessage'] = (
		streamMessage,
	) => {
		if (disabled) {
			return false;
		}
		// Bypasses the streaming parser: inside an open expand or follow-ups block the
		// placeholder would be captured as raw block text, so it wouldn't render as a card
		// but would still take a slot, shifting every later card. The card shows just after
		// the block instead. Not forwarded to promptStreamHook.onAnswerPart: it's
		// synthetic text, not model output.
		if (streamMessage.type === 'ANSWER_UI_PART') {
			const placement = tracker.onUiPart(streamMessage);
			const wrote =
				!!placement &&
				tryWrite(streamMessage.type, () =>
					setMessages(
						conversationId,
						addAnswerPartWithMarkdownDataMap({
							chat: getMessages(conversationId),
							answerId: getAnswerId(),
							answerPart: {
								type: 'ANSWER_PART',
								message: { content: placement.placeholder, role: 'ASSISTANT' },
								millisOffset: streamMessage.millisOffset,
							},
							markdownData: placement.markdownData,
							// Same write as the placeholder, so a loading card always has its type.
							extraMarkdownData: placement.extraMarkdownData,
							useIndex: answerIndex,
						}),
					),
				);
			if (wrote) {
				// Only once the card is in the store: this clears the thinking indicator.
				onUiPartPlaced();
			}
			return wrote;
		}
		if (streamMessage.type === 'ANSWER_UI_CONTENT') {
			const slotUpdate = tracker.onUiContent(streamMessage);
			return (
				!!slotUpdate &&
				tryWrite(streamMessage.type, () =>
					setMessages(
						conversationId,
						setAnswerUiSlots({
							chat: getMessages(conversationId),
							answerId: getAnswerId(),
							updates: [slotUpdate],
							useIndex: answerIndex,
						}),
					),
				)
			);
		}
		return false;
	};

	// After a write failure, close reports would only restate it (payloads the tracker
	// never saw), so they're skipped.
	const onFinalResponse: AnswerUiPartsStreamHandler['onFinalResponse'] = () => {
		const closeResult = tracker.close('final');
		if (closeResult && !disabled) {
			reportClose(closeResult);
		}
	};

	const onStreamEnd: AnswerUiPartsStreamHandler['onStreamEnd'] = () => {
		const closeResult = tracker.close('aborted');
		if (!closeResult) {
			return;
		}
		if (!disabled) {
			reportClose(closeResult);
		}
		if (closeResult.slotUpdates.length === 0) {
			return;
		}
		// Still settle after a write failure, so cards placed before it don't stay loading.
		try {
			setMessages(
				conversationId,
				setAnswerUiSlots({
					chat: getMessages(conversationId),
					answerId: getAnswerId(),
					updates: closeResult.slotUpdates,
					useIndex: answerIndex,
				}),
			);
		} catch (error) {
			// Runs in `finally`: never mask the stream's own error. A failure after a
			// reported write failure is the same problem, so it isn't reported again.
			if (!disabled) {
				void logException({ exception: error, name: 'AnswerUiPartsAbortCleanupError' });
			}
		}
	};

	return {
		handleStreamMessage,
		observeParserMarkdownData: tracker.onParserMarkdownData,
		onFinalResponse,
		onStreamEnd,
	};
};
